import { useCallback, useEffect, useRef, useState } from "react";
import { Play, Pause, Loader2, SkipBack, SkipForward, Gauge, Moon } from "lucide-react";
import { toast } from "sonner";
import { fetchAudio } from "@/lib/reader";

const SLEEP_OPTIONS = [
  { id: "15", label: "15 minutes", minutes: 15 },
  { id: "30", label: "30 minutes", minutes: 30 },
  { id: "60", label: "60 minutes", minutes: 60 },
  { id: "chapter", label: "End of chapter" },
];

function SleepMenu({ sleep, now, onChoose }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (e.type === "keydown" ? e.key === "Escape" : !ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);
  const label = sleep?.kind === "min" ? `${Math.max(1, Math.ceil((sleep.endsAt - now) / 60000))}m` : sleep?.kind === "chapter" ? "Ch." : null;
  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="sleep-timer-button"
        aria-label="Sleep timer"
        className={`flex items-center gap-1 font-mono-ui text-[10px] transition-colors ${sleep ? "text-primary-gold" : "text-soft hover:text-text-primary"}`}
      >
        <Moon className={`w-4 h-4 ${sleep ? "fill-current" : ""}`} />
        {label && <span data-testid="sleep-timer-remaining">{label}</span>}
      </button>
      {open && (
        <div className="absolute bottom-10 right-0 sm:left-1/2 sm:-translate-x-1/2 sm:right-auto w-48 border border-white/15 bg-[#121212] shadow-[0_20px_50px_rgba(0,0,0,0.7)] py-2" data-testid="sleep-timer-menu">
          <p className="px-4 pb-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-primary-gold">Sleep timer</p>
          {SLEEP_OPTIONS.map((o) => (
            <button key={o.id} onClick={() => { onChoose(o); setOpen(false); }} data-testid={`sleep-option-${o.id}`} className="w-full text-left px-4 py-2 text-sm hover:bg-white/5 transition-colors">
              {o.label}
            </button>
          ))}
          {sleep && (
            <button onClick={() => { onChoose(null); setOpen(false); }} data-testid="sleep-option-off" className="w-full text-left px-4 py-2 text-sm text-soft hover:bg-white/5 hover:text-[#d6453a] transition-colors">
              Turn off
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const RATES = [0.85, 1, 1.15, 1.3];
const fmt = (s) => (Number.isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "0:00");

function VoiceToggle({ voice, onVoice }) {
  return (
    <div className="flex border border-white/15" data-testid="voice-toggle">
      {["male", "female"].map((v) => (
        <button
          key={v}
          onClick={() => onVoice(v)}
          data-testid={`voice-${v}`}
          className={`px-2 sm:px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.18em] transition-colors ${
            voice === v ? "bg-primary-gold text-[#0B0C10]" : "text-soft hover:text-text-primary"
          }`}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

export default function NarrationBar({ bookId, page, total, voice, onVoice, onPageChange, onAuthError, onPlayingChange, chapters = [] }) {
  const audioRef = useRef(null);
  const cache = useRef(new Map());
  const loadedKey = useRef(null);
  const wantPlay = useRef(false);
  const [status, setStatus] = useState("idle");
  const [time, setTime] = useState({ cur: 0, dur: 0 });
  const [rateIdx, setRateIdx] = useState(1);
  const [sleep, setSleep] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    onPlayingChange?.(status === "playing");
  }, [status, onPlayingChange]);

  const stopForSleep = useCallback(() => {
    wantPlay.current = false;
    audioRef.current?.pause();
    setSleep(null);
    toast("Sleep timer — narration paused. Sweet dreams.");
  }, []);

  useEffect(() => {
    if (sleep?.kind !== "min") return;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= sleep.endsAt) stopForSleep();
    }, 1000);
    return () => clearInterval(id);
  }, [sleep, stopForSleep]);

  const chooseSleep = (option) => {
    if (!option) {
      setSleep(null);
      return toast("Sleep timer off.");
    }
    if (option.minutes) {
      setNow(Date.now());
      setSleep({ kind: "min", endsAt: Date.now() + option.minutes * 60000 });
      return toast(`Narration will stop in ${option.minutes} minutes.`);
    }
    const next = chapters.find((c) => c.page > page);
    setSleep({ kind: "chapter" });
    toast(`Narration will stop at the end of this chapter${next ? ` (page ${next.page - 1})` : ""}.`);
  };
  const key = `${page}-${voice}`;
  const keyRef = useRef(key);
  keyRef.current = key;

  const getUrl = useCallback(
    (p, v, prefetch = false) => {
      const k = `${p}-${v}`;
      if (!cache.current.has(k)) {
        const promise = fetchAudio(bookId, p, v, prefetch).then((blob) => URL.createObjectURL(blob));
        promise.catch(() => cache.current.delete(k));
        cache.current.set(k, promise);
      }
      return cache.current.get(k);
    },
    [bookId]
  );

  const start = useCallback(
    async (p, v) => {
      const k = `${p}-${v}`;
      setStatus("loading");
      try {
        const url = await getUrl(p, v);
        if (keyRef.current !== k || !wantPlay.current) return;
        const audio = audioRef.current;
        audio.src = url;
        audio.playbackRate = RATES[rateIdx];
        loadedKey.current = k;
        await audio.play();
        if (p < total) getUrl(p + 1, v, true).catch(() => {});
      } catch (e) {
        if (keyRef.current !== k) return;
        wantPlay.current = false;
        if ([401, 402, 409].includes(e?.response?.status)) onAuthError(e);
        else setStatus("error");
      }
    },
    [getUrl, rateIdx, total, onAuthError]
  );

  useEffect(() => {
    const audio = audioRef.current;
    if (loadedKey.current === key) return;
    audio.pause();
    audio.removeAttribute("src");
    loadedKey.current = null;
    setTime({ cur: 0, dur: 0 });
    if (wantPlay.current) start(page, voice);
    else setStatus("idle");
  }, [key, page, voice, start]);

  useEffect(() => {
    const urls = cache.current;
    return () => urls.forEach((p) => p.then((u) => URL.revokeObjectURL(u)).catch(() => {}));
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (status === "playing") {
      wantPlay.current = false;
      audio.pause();
      return;
    }
    wantPlay.current = true;
    if (loadedKey.current === key) audio.play();
    else start(page, voice);
  };

  const cycleRate = () => {
    const next = (rateIdx + 1) % RATES.length;
    setRateIdx(next);
    audioRef.current.playbackRate = RATES[next];
  };

  const onEnded = () => {
    if (sleep?.kind === "chapter" && (page >= total || chapters.some((c) => c.page === page + 1))) return stopForSleep();
    if (page < total) onPageChange(page + 1);
    else {
      wantPlay.current = false;
      setStatus("idle");
    }
  };

  return (
    <div className="fixed bottom-0 inset-x-0 z-40 border-t border-white/10 bg-[#0B0C10]/90 backdrop-blur-xl" data-testid="narration-bar">
      <audio
        ref={audioRef}
        onPlay={() => setStatus("playing")}
        onPause={() => setStatus((s) => (s === "loading" ? s : "paused"))}
        onEnded={onEnded}
        onTimeUpdate={(e) => setTime({ cur: e.target.currentTime, dur: e.target.duration })}
        onLoadedMetadata={(e) => setTime({ cur: 0, dur: e.target.duration })}
      />
      <input
        type="range"
        min={0}
        max={time.dur || 0}
        step={0.5}
        value={time.cur}
        onChange={(e) => (audioRef.current.currentTime = Number(e.target.value))}
        className="narration-progress w-full block"
        aria-label="Narration position"
        data-testid="narration-progress"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 sm:gap-5">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} className="text-soft hover:text-text-primary disabled:opacity-30" data-testid="narration-prev" aria-label="Previous page">
          <SkipBack className="w-5 h-5" />
        </button>
        <button
          onClick={toggle}
          data-testid="narration-play-toggle"
          aria-label={status === "playing" ? "Pause" : "Play"}
          className="w-12 h-12 rounded-full bg-primary-gold text-[#0B0C10] flex items-center justify-center hover:scale-105 transition-transform shrink-0"
        >
          {status === "loading" ? <Loader2 className="w-5 h-5 animate-spin" /> : status === "playing" ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= total} className="text-soft hover:text-text-primary disabled:opacity-30" data-testid="narration-next" aria-label="Next page">
          <SkipForward className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-soft font-mono-ui truncate" data-testid="narration-status">
            {status === "loading"
              ? "Preparing narration — first listen of a page can take up to 30s…"
              : status === "error"
              ? "Narration unavailable right now — press play to retry."
              : status === "idle"
              ? `Listen to page ${page}`
              : <>{fmt(time.cur)}<span className="hidden sm:inline"> / {fmt(time.dur)} · page {page} of {total}</span></>}
          </p>
        </div>
        <button onClick={cycleRate} className="flex items-center gap-1 sm:gap-1.5 font-mono-ui text-[10px] text-soft hover:text-text-primary shrink-0" data-testid="narration-speed" aria-label="Playback speed">
          <Gauge className="w-4 h-4 hidden sm:block" /> {RATES[rateIdx]}×
        </button>
        <SleepMenu sleep={sleep} now={now} onChoose={chooseSleep} />
        <VoiceToggle voice={voice} onVoice={onVoice} />
      </div>
    </div>
  );
}
